import {Icon} from '@udemy-v2/icons';
import {BlockList} from '@udemy-v2/react-core-components';
import {ShowMore} from '@udemy-v2/react-reveal-components';
import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';

import {useI18n} from '@udemy-v2/i18n';
import styles from './what-you-will-learn.module.scss';

interface WhatYouWillLearnProps {
    objectives: string[];
    summaryLimit?: number;
    titleTypography?: 'ud-heading-lg' | 'ud-heading-xl';
    listLayoutClassName?: string;
}

/**
 * WhatYouWillLearn component displays a list of learning objectives with a show more/less functionality
 * @param {WhatYouWillLearnProps} props - The component props
 */
export function WhatYouWillLearn({
    objectives = [],
    summaryLimit = 10,
    titleTypography = 'ud-heading-xl',
    listLayoutClassName = styles['objectives-list-two-column-layout'],
}: WhatYouWillLearnProps) {
    const [isShowingMore, setIsShowingMore] = useState(false);
    const [isAnyObjectiveTruncated, setIsAnyObjectiveTruncated] = useState(false);
    const objectivesRefs = useRef<Record<string, HTMLSpanElement | null>>({});
    const {gettext} = useI18n();

    useEffect(() => {
        // Check if any objective is truncated on mount
        const isTruncated = objectives.some((objective) => {
            const itemRef = objectivesRefs.current[objective];
            return itemRef && itemRef.scrollHeight > itemRef.clientHeight;
        });
        setIsAnyObjectiveTruncated(isTruncated);
    }, [objectives]);

    const handleShowMoreToggle = () => {
        setIsShowingMore((prev) => !prev);
    };

    const setRef = (id: string) => (ref: HTMLSpanElement | null) => {
        objectivesRefs.current[id] = ref;
    };

    const renderObjectives = () => {
        const objectiveItemClassName = !isShowingMore ? styles['objective-item'] : undefined;

        return (
            <BlockList
                size="small"
                padding="tight"
                className={classNames(styles['objectives-list'], listLayoutClassName)}
            >
                {objectives.map((objective) => (
                    <BlockList.Item
                        data-purpose="objective"
                        key={objective}
                        icon={<Icon glyph="tick" label={false} />}
                    >
                        <span className={objectiveItemClassName} ref={setRef(objective)}>
                            {objective}
                        </span>
                    </BlockList.Item>
                ))}
            </BlockList>
        );
    };

    if (!objectives.length) {
        return null;
    }

    const hiddenObjectivesCount = objectives.length - summaryLimit;

    return (
        <>
            <span className="in-page-offset-anchor" id="what-you-will-learn" />
            <div className={classNames('component-margin', styles['what-you-will-learn'])}>
                <h2 className={classNames(titleTypography, styles['title'])}>
                    {gettext("What you'll learn")}
                </h2>
                {hiddenObjectivesCount > 0 || isAnyObjectiveTruncated ? (
                    <ShowMore
                        className={styles['content-spacing']}
                        collapsedHeight={228}
                        withGradient={true}
                        onToggle={handleShowMoreToggle}
                    >
                        {renderObjectives()}
                    </ShowMore>
                ) : (
                    <div className={styles['content-spacing']}>{renderObjectives()}</div>
                )}
            </div>
        </>
    );
}
